/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
'use client';

import classNames from 'classnames';
import {default as Image} from '@/components/global/images/resized-cdn-image';
import React, { useEffect, useRef, useState } from 'react';

import { StrapiStop } from '@/apis/strapi';
import { doTranslation } from '@/app/i18n/client';
import ButtonTertiary from '@/components/global/buttons/button-tertiary';
import PillIcon from '@/components/global/pills/pill-icon';
import dynamic from 'next/dynamic';
const Tooltip = dynamic(() => import('@/components/global/tooltip/tooltip'), {
  ssr: false
});
import { localeSpecificPath } from '@/utils/localeUtils';

import styles from '@/components/schedule-results/bus-station-details.module.scss';

import addressBrand from '@/public/assets/images/global/bus-station/address-brand.svg';
import addressPrimary from '@/public/assets/images/global/bus-station/address-primary.svg';
import circleInfoDark from '@/public/assets/images/global/bus-station/circle-info-dark.svg';
import circleInfoLight from '@/public/assets/images/global/bus-station/circle-info-light.svg';
import timePrimary from '@/public/assets/images/global/bus-station/time-primary.svg';

type Props = {
  lng: string;
  destinationCityName: string;
  destinationStateAbbrev: string;
  destinationStops: StrapiStop[] | null;
};

const BusStationDetails = (props: Props): React.JSX.Element => {
  const { destinationCityName, destinationStateAbbrev, destinationStops, lng } = props;
  const { t } = doTranslation(lng);
  const [showLoadMore, setShowLoadMore] = useState(destinationStops ? destinationStops.length > 4 : false);

  const ref = useRef<Array<HTMLDivElement | null>>([]);

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [markerIndex, setMarkerIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleClickFocus = (event: any) => {
      const isOutside = !ref.current.some((card) => card?.contains(event.target));
      const isShowMoreFocused = ref.current.some((card) => card?.querySelector('#more')?.matches(':focus'));

      if (isOutside || isShowMoreFocused) {
        setMarkerIndex(null);
        setActiveIndex(null);
      }
      if (activeIndex && activeIndex != markerIndex) {
        setMarkerIndex(activeIndex);
      }
    };

    document.addEventListener('focus', handleClickFocus, true);
    document.addEventListener('click', handleClickFocus, true);

    return () => {
      document.removeEventListener('focus', handleClickFocus, true);
      document.removeEventListener('click', handleClickFocus, true);
    };
  }, [activeIndex, markerIndex]);

  useEffect(() => {
    const focusEventListner = (event: any) => {
      const currentCard = ref.current.filter((card) => card && card.contains(event.target)) || [];
      if (event.target.getAttribute('data-card-index') != null) {
        setActiveIndex(null);
      } else if (currentCard.length && event.target != currentCard[0]) {
        const currentIndex = currentCard[0]?.getAttribute('data-card-index');
        const parsedIndex = currentIndex ? parseInt(currentIndex, 10) : null;
        if (parsedIndex !== null && parsedIndex !== activeIndex) {
          setActiveIndex(parsedIndex);
        }
      }
    };
    document?.addEventListener('focus', focusEventListner, true);
    return () => {
      document?.removeEventListener('focus', focusEventListner, true);
    };
  }, [activeIndex]);

  useEffect(() => {
    if (!showLoadMore) {
      ref.current[4]?.focus();
    }
  }, [showLoadMore]);

  const renderPill = (stationType: string, index: number) => {
    if (stationType === 'Curbside Stop') {
      return (
        <PillIcon primary label={stationType || t('Not available at this time')} tabIndex={-1}>
          <Tooltip
            ariaLabelOpen={t('Curbside Stop more info')}
            ariaLabelClose={t('Curbside Stop more info')}
            content={t(
              'Trailways bus stops are designated places where buses pick up and drop off passengers. Bus stops are sometimes located at other businesses, such as a gas station, or at a road side shelter.'
            )}
            id={`id-${index}`}
            place="right"
            wrap={true}
            tabIndex={activeIndex === index ? 0 : -1}
          >
            <Image alt={t('Info')} height="13" src={circleInfoDark} width="13" />
          </Tooltip>
        </PillIcon>
      );
    } else if (stationType === 'Flag Stop') {
      return (
        <PillIcon secondary label={stationType || t('Not available at this time')} tabIndex={-1}>
          <Tooltip
            ariaLabelOpen={t('Flag Stop more info')}
            ariaLabelClose={t('Flag Stop more info')}
            content={t(
              'Flag stops are road side pickup locations. Vehicles only stop at these locations when passengers are present. It is important that passengers arrive at flag stops before the time of departure and wave at the bus as it approaches.'
            )}
            id={`id-${index}`}
            place="right"
            wrap={true}
            tabIndex={activeIndex === index ? 0 : -1}
          >
            <Image alt={t('Info')} height="13" src={circleInfoDark} width="13" />
          </Tooltip>
        </PillIcon>
      );
    } else if (stationType === 'Bus Station') {
      return (
        <PillIcon dark label={stationType || t('Not available at this time')} tabIndex={-1}>
          <Tooltip
            ariaLabelOpen={t('Bus Station more info')}
            ariaLabelClose={t('Bus Station more info')}
            content={t(
              'Trailways Bus Stations are facilities that have waiting areas and may also include ticket counters, restrooms, and sometimes even shops or cafes.'
            )}
            dark
            id={`id-${index}`}
            place="right"
            wrap={true}
            tabIndex={activeIndex === index ? 0 : -1}
          >
            <Image alt={t('Info')} height="13" src={circleInfoLight} width="13" />
          </Tooltip>
        </PillIcon>
      );
    }
  };

  const handleKeyDown = (event: any, index: number) => {
    const element = ref.current[index];
    const focusHeading = element?.querySelector('h3')?.matches(':focus');
    const focusViewMore = element?.querySelector('#link')?.matches(':focus');
    switch (event.key) {
      // Focusing on individual cards when tabbing over it
      case 'Tab':
        if (event.shiftKey && focusHeading) {
          setActiveIndex(null);
          return;
        }
        if (!event.shiftKey && focusViewMore) {
          setActiveIndex(null);
          return;
        }
        break;
      // Enhancing focus upon entering the card.
      case 'Enter':
      case ' ':
        // Setting active index and focusing on 'h3' element if no active index is set
        if (activeIndex == null) {
          setActiveIndex(index);
          ref.current[index]?.querySelector('h3')?.focus();
          event.preventDefault();
          return;
        }
        const linkElement = element?.querySelector('#link') as HTMLAnchorElement | null;
        if (focusViewMore && linkElement) {
          linkElement?.click();
          event.preventDefault();
          return;
        }
        const phoneElement = event.target?.id === 'phone' ? event.target : null;
        if (phoneElement && phoneElement.matches(':focus')) {
          phoneElement?.click();
          event.preventDefault();
          return;
        }
        if (focusHeading) {
          event.preventDefault();
          return;
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (activeIndex !== null) {
          event.preventDefault();
          return;
        }
        break;
      default:
        break;
    }
  };

  const renderNotices = (stop: StrapiStop) => {
    const notice = stop.attributes.stop_advanced_notice;
    const hours = stop.attributes.stop_show_open_24x7;
    if (notice || hours) {
      return (
        <div className={styles.messages}>
          {hours && (
            <div className={styles.pills}>
              <Image alt={t('clock')} height="16" src={timePrimary} width="16" aria-hidden="true" loading='lazy'/>
              <div className={styles.primary} tabIndex={-1}>
                {t('24 Hours')}
              </div>
            </div>
          )}
          {hours && notice && <div className={styles.divider}></div>}
          {notice && (
            <div className={styles.pills}>
              <div className={styles.brand} tabIndex={-1}>
                {t('Advance Notice Required')}
              </div>
            </div>
          )}
        </div>
      );
    }
  };
  const renderAddress = (stop: StrapiStop) => {
    const combinedAddress = [stop?.attributes?.stop_address1, stop?.attributes?.stop_address2]
      .filter(Boolean)
      .join(', ');
    const statePostal = [
      stop.attributes.cities?.data[0].attributes.state?.data.attributes.state_id,
      stop?.attributes?.stop_postal_code
    ]
      .filter(Boolean)
      .join(' ');
    const cityStatePostal = stop.attributes.cities?.data[0].attributes.city_name
      ? `${stop.attributes.cities?.data[0].attributes.city_name}, ${statePostal}`
      : statePostal;
    return (
      <dd
        className={classNames({
          [styles.text]: true,
          [styles['no-outline']]: true
        })}
        tabIndex={-1}
      >
        {combinedAddress || statePostal || cityStatePostal ? (
          <div>
            {stop?.attributes?.stop_address1 && (
              <span className={styles.avoidwrap}>{stop?.attributes?.stop_address1}&nbsp;</span>
            )}
            {stop?.attributes?.stop_address2 && (
              <span className={styles.avoidwrap}>{stop?.attributes?.stop_address2}</span>
            )}
            <span className={styles['city-state-postal']}>
              {combinedAddress && cityStatePostal && <br />}
              {cityStatePostal && <span>{cityStatePostal}</span>}
            </span>
          </div>
        ) : (
          t('Not available at this time')
        )}
      </dd>
    );
  };

  return (
    <div aria-labelledby="bus-station-information" className={styles.container} role="region">
      <h2 className={styles.heading} id="bus-station-information">
        {t(`Bus Station Information for {{ destination }}, {{ state }}`, {
          destination: destinationCityName,
          state: destinationStateAbbrev
        })}
      </h2>
      <div className={styles.grid}>
        {destinationStops &&
          (showLoadMore ? destinationStops.slice(0, 4) : destinationStops).map((stop: StrapiStop, index: number) => {
            const stationName = stop.attributes.stop_name;
            const city = stop.attributes.cities?.data[0]?.attributes.city_name;
            const state = stop.attributes.cities?.data[0]?.attributes.state?.data?.attributes.state_id;
            const postalCode = stop.attributes.stop_postal_code;
            const location = [`${city ? city + (state || postalCode ? ',' : '') : ''}`, state, postalCode]
              .filter(Boolean)
              .join(' ');
            const stationType = stop.attributes.stop_type;
            const stopSlug = stop.attributes.stop_slug;
            return (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions
              <div
                key={index}
                className={classNames({
                  [styles.card]: true,
                  [styles.active]: index === activeIndex
                })}
                data-card-index={index}
                tabIndex={0}
                ref={(el) => {
                  ref.current[index] = el;
                }}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={() => {
                  if (!activeIndex) {
                    setMarkerIndex(index);
                  }
                }}
                onClick={(e: any) => {
                  // clear the active card focus when clicking on a different card
                  if (activeIndex !== null && e.currentTarget !== ref.current[activeIndex]) {
                    setActiveIndex(null);
                    setMarkerIndex(index);
                    // This is intended for JAWS to set focus on the active card.
                  } else if (activeIndex === null && e.target.classList.contains('sr-only')) {
                    setActiveIndex(index);
                    ref.current[index]?.querySelector('h3')?.focus();
                  }
                }}
              >
                <span className="sr-only">
                  {t(
                    '{{- stationName }} {{ stationType }}, press enter to explore more information, or press tab to move to the next bus station',
                    {
                      stationName,
                      stationType
                    }
                  )}
                </span>
                <div className={styles.content}>
                  <div className={styles.block}>
                    <div className={styles.details}>
                      <h3
                        className={classNames({
                          [styles.name]: true,
                          [styles.active]: index === activeIndex
                        })}
                        tabIndex={activeIndex === index ? 0 : -1}
                      >
                        {stationName || t('Not available at this time')}
                      </h3>
                      <span className="sr-only">
                        {t('{{ stationType }}, use arrow keys or tab to explore more about this bus station', {
                          stationType
                        })}
                      </span>
                      <p className={styles.address}>{location || t('Not available at this time')}</p>
                      {renderNotices(stop)}
                    </div>
                    <div className="station-type bus-stop">
                      <span aria-hidden="true" className="sr-only">
                        {stationType}
                      </span>
                      {renderPill(stationType, index)}
                    </div>
                  </div>
                  <div className={styles.info}>
                    <div className={styles.row}>
                      <dl className={styles.address}>
                          <dt>
                            <div className={styles.label}>
                              <div className={styles.icon}>
                                <Image
                                  alt={t('pin')}
                                  src={index === markerIndex ? addressBrand : addressPrimary}
                                  aria-hidden="true"
                                  loading='lazy'
                                />
                              </div>
                              <div className={styles.field} tabIndex={-1}>
                                {t('Address')}
                              </div>
                            </div>
                          </dt>
                          {renderAddress(stop)}
                      </dl>
                    </div>
                    <a
                      className={classNames({
                        [styles.more]: true,
                        ['gtm-bus_station_details']: true
                      })}
                      tabIndex={activeIndex === index ? 0 : -1}
                      id="link"
                      href={localeSpecificPath(`/bus-station/${stopSlug}/detail/`, lng)}
                    >
                      <span>{stationType === 'Bus Station' ? t('Bus Station Details') : t('Bus Stop Details')}</span>
                      <span className="sr-only">
                        {t('about {{- stationName }} {{ stationType }}', { stationName, stationType })}
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {showLoadMore && (
        <div className={styles.link}>
          <ButtonTertiary dark onClick={() => setShowLoadMore(false)} id="more">
            {t('Show More Bus Stations')}
          </ButtonTertiary>
        </div>
      )}
    </div>
  );
};

export default BusStationDetails;
